import { HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';

/**
 * Stavové kódy, které si u tohohle requestu ohlásí volající sám — error interceptor je
 * přejde mlčením. Ostatní stavy téhož requestu hlásí dál.
 *
 * ⚠ Je to pro odpověď, která **není chyba**: 409 u importu testu nebo šablony certifikátu
 * znamená „kurz už ho má, nahradit?" a obrazovka na to ukáže otázku. Interceptor by k ní
 * přidal červenou hlášku s toutéž větou (EL-2307). Celou URL do `SELF_HANDLED_URLS` dát
 * nejde — vadný soubor (400) nebo pád serveru má interceptor ohlásit.
 */
export const SELF_HANDLED_STATUSES = new HttpContextToken<readonly number[]>(() => []);

/** Kontext requestu, jehož dané stavové kódy si volající ohlásí sám. */
export function selfHandledStatuses(...statuses: number[]): HttpContext {
  return new HttpContext().set(SELF_HANDLED_STATUSES, statuses);
}

/**
 * Obecná věta, kterou k validační chybě přidává ASP.NET. Uživateli neřekne nic —
 * podstatné je až to, co je v `errors`.
 */
const OBECNA_VALIDACE = /one or more validation errors occurred/i;

/** Kolik podrobností se do hlášky vejde, aby zůstala čitelná. */
const MAX_PODROBNOSTI = 3;

/**
 * Text hlášky, kterou poslal server.
 *
 * `HttpExceptionMiddleware` serializuje `ProblemDetails` bez naming policy, takže klíč
 * je `Title` s velkým písmenem; malé je tu pro případ, že by se serializace sjednotila.
 *
 * ⚠ **Validační 400 má `Title` k ničemu.** ASP.NET do něj dá anglické „One or more
 * validation errors occurred." a co je vlastně špatně, leží až v `errors`. Autorovi se
 * pak sice hláška ukázala, ale nedozvěděl se z ní nic — přesně to potkalo ukládání bloku
 * ve skladateli (EL-2235). Proto se podrobnosti vytáhnou a obecný titulek se zahodí.
 *
 * Vrácené `null` znamená „server nic použitelného neposlal"; volající pak dosadí
 * svou přeloženou větu.
 */
export function serverMessage(error: unknown): string | null {
  const body = (error as HttpErrorResponse)?.error;

  if (!body) {
    return null;
  }

  const podrobnosti = validationMessages(body);

  if (podrobnosti.length > 0) {
    return podrobnosti.slice(0, MAX_PODROBNOSTI).join(' ');
  }

  const title: string | null = body.Title ?? body.title ?? null;

  return title && !OBECNA_VALIDACE.test(title) ? title : null;
}

/**
 * Věty z `errors` — slovníku „pole → seznam hlášek", který posílá validace modelu.
 *
 * ⚠ Duplicity se vyhazují: stejná hláška u tří polí je pro čtenáře jedna informace,
 * ne tři.
 */
function validationMessages(body: { errors?: Record<string, unknown> }): string[] {
  const errors = body.errors;

  if (!errors || typeof errors !== 'object') {
    return [];
  }

  const vety = Object.values(errors)
    .flatMap((hodnota) => (Array.isArray(hodnota) ? hodnota : [hodnota]))
    .filter((veta): veta is string => typeof veta === 'string' && veta.trim().length > 0)
    .map((veta) => veta.trim());

  return [...new Set(vety)];
}
